import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import OpportunityModal from '../components/crm/OpportunityModal';
import ActivityModal from '../components/crm/ActivityModal';
import {
  StageBadge, QuoteStatusBadge, activityTypeInfo, formatMoney, formatDate, formatDateTime, isOverdue, OPEN_STAGES,
} from '../components/crm/crmConstants';

const TABS = [
  { key: 'activities', label: 'Aktiviteler' },
  { key: 'opportunities', label: 'Fırsatlar' },
  { key: 'quotes', label: 'Teklifler' },
  { key: 'contacts', label: 'Kişiler' },
];

const emptyContact = { name: '', title: '', phone: '', email: '', is_primary: false, notes: '' };

export default function CustomerDetail() {
  const { id } = useParams();
  const customerId = parseInt(id);
  const navigate = useNavigate();
  const [tab, setTab] = useState('activities');
  const [customer, setCustomer] = useState(null);
  const [summary, setSummary] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [showSystem, setShowSystem] = useState(true);

  const [oppModal, setOppModal] = useState(null);       // null | 'new' | opportunity
  const [activityModal, setActivityModal] = useState(null); // null | { activity?, defaults? }
  const [contactForm, setContactForm] = useState(null); // null | contact (with optional id)

  const fetchAll = useCallback(async () => {
    const [c, s, ct, o, q, a] = await Promise.all([
      api.get(`/api/customers/${customerId}`),
      api.get(`/api/crm/customers/summaries?customer_id=${customerId}`),
      api.get(`/api/crm/customers/${customerId}/contacts`),
      api.get(`/api/crm/opportunities?customer_id=${customerId}`),
      api.get(`/api/quotes/?customer_id=${customerId}`),
      api.get(`/api/crm/activities?customer_id=${customerId}`),
    ]);
    setCustomer(c.data);
    setSummary(s.data[0]);
    setContacts(ct.data);
    setOpportunities(o.data);
    setQuotes(q.data);
    setActivities(a.data);
  }, [customerId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleDone = async (a) => {
    await api.put(`/api/crm/activities/${a.id}`, { is_done: !a.is_done });
    fetchAll();
  };

  const deleteActivity = async (a) => {
    if (!window.confirm('Aktiviteyi silmek istediğinize emin misiniz?')) return;
    await api.delete(`/api/crm/activities/${a.id}`);
    fetchAll();
  };

  const deleteOpportunity = async (o) => {
    if (!window.confirm(`"${o.title}" fırsatı silinecek. Bağlı teklifler silinmez, sadece bağlantı kaldırılır. Devam?`)) return;
    await api.delete(`/api/crm/opportunities/${o.id}`);
    fetchAll();
  };

  const saveContact = async (e) => {
    e.preventDefault();
    try {
      const { id: contactId, ...data } = contactForm;
      const payload = {
        name: data.name, title: data.title || null, phone: data.phone || null,
        email: data.email || null, is_primary: !!data.is_primary, notes: data.notes || null,
      };
      if (contactId) await api.put(`/api/crm/contacts/${contactId}`, payload);
      else await api.post(`/api/crm/customers/${customerId}/contacts`, payload);
      setContactForm(null);
      fetchAll();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || 'Bilinmeyen hata'));
    }
  };

  const deleteContact = async (c) => {
    if (!window.confirm(`${c.name} silinecek. Devam?`)) return;
    await api.delete(`/api/crm/contacts/${c.id}`);
    fetchAll();
  };

  if (!customer) return <div className="text-gray-400">Yükleniyor...</div>;

  const pipelineText = summary && Object.keys(summary.pipeline_values).length > 0
    ? Object.entries(summary.pipeline_values).map(([cur, v]) => formatMoney(v, cur)).join(' + ')
    : '—';
  const openTasks = activities.filter(a => a.type === 'task' && !a.is_done);
  const visibleActivities = activities.filter(a => showSystem || a.type !== 'system');
  const oppTitle = (oid) => opportunities.find(o => o.id === oid)?.title;

  return (
    <div>
      <div className="flex justify-between items-start mb-6">
        <div>
          <button onClick={() => navigate('/customers')} className="text-gray-500 hover:text-gray-700 text-sm mb-1">← Müşteriler</button>
          <h2 className="text-2xl font-bold text-gray-800">{customer.company_name}</h2>
          <div className="text-sm text-gray-500 mt-1 flex flex-wrap gap-x-4">
            {customer.contact_name && <span>👤 {customer.contact_name}</span>}
            {customer.phone && <a href={`tel:${customer.phone}`} className="hover:text-blue-600">📞 {customer.phone}</a>}
            {customer.email && <a href={`mailto:${customer.email}`} className="hover:text-blue-600">✉️ {customer.email}</a>}
            <span>💱 {customer.currency} · {customer.payment_terms} gün</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setActivityModal({ defaults: { type: 'call' } })} className="bg-white border text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm font-medium">📝 Aktivite</button>
          <button onClick={() => setActivityModal({ defaults: { type: 'task' } })} className="bg-white border text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm font-medium">☑️ Görev</button>
          <button onClick={() => setOppModal('new')} className="bg-white border text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-50 text-sm font-medium">💼 Fırsat</button>
          <button onClick={() => navigate(`/quotes/new?customer_id=${customerId}`)} className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">+ Teklif</button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Açık Fırsatlar" value={summary?.open_opportunities ?? 0} sub={pipelineText} />
        <SummaryCard label="Kazanılan Fırsatlar" value={summary?.won_opportunities ?? 0} />
        <SummaryCard label="Teklifler" value={summary?.quote_count ?? 0} sub={`${summary?.approved_quote_count ?? 0} onaylandı`} />
        <SummaryCard label="Açık Görevler" value={summary?.open_tasks ?? 0}
          sub={summary?.overdue_tasks ? `${summary.overdue_tasks} gecikmiş` : `Son temas: ${formatDateTime(summary?.last_activity_at)}`}
          alert={summary?.overdue_tasks > 0} />
      </div>

      <div className="flex gap-1 border-b mb-4">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
            <span className="ml-1 text-xs text-gray-400">
              {{ activities: activities.filter(a => a.type !== 'system').length, opportunities: opportunities.length, quotes: quotes.length, contacts: contacts.length }[t.key]}
            </span>
          </button>
        ))}
      </div>

      {tab === 'activities' && (
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="flex justify-between items-center px-4 py-3 border-b">
              <h3 className="font-semibold text-gray-700">Zaman Akışı</h3>
              <label className="text-xs text-gray-500 flex items-center gap-1">
                <input type="checkbox" checked={showSystem} onChange={e => setShowSystem(e.target.checked)} /> Sistem kayıtlarını göster
              </label>
            </div>
            <div className="divide-y">
              {visibleActivities.map(a => (
                <ActivityRow key={a.id} a={a} onToggle={toggleDone} onEdit={() => setActivityModal({ activity: a })} onDelete={deleteActivity} navigate={navigate} />
              ))}
              {visibleActivities.length === 0 && <div className="text-center py-10 text-gray-400 text-sm">Henüz aktivite yok</div>}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow h-fit">
            <div className="px-4 py-3 border-b font-semibold text-gray-700">Açık Görevler</div>
            <div className="divide-y">
              {openTasks.map(t => (
                <div key={t.id} className="px-4 py-3 flex gap-2 items-start">
                  <input type="checkbox" className="mt-1" checked={false} onChange={() => toggleDone(t)} />
                  <div className="text-sm">
                    <div className="font-medium text-gray-800">{t.subject}</div>
                    <div className={`text-xs ${isOverdue(t) ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                      {t.due_date ? formatDate(t.due_date) : 'Tarihsiz'}{t.assigned_to_name ? ` · ${t.assigned_to_name}` : ''}
                    </div>
                  </div>
                </div>
              ))}
              {openTasks.length === 0 && <div className="text-center py-6 text-gray-400 text-sm">Açık görev yok</div>}
            </div>
          </div>
        </div>
      )}

      {tab === 'opportunities' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Fırsat</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Aşama</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Tutar</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Olasılık</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Kapanış</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Teklifler</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Sorumlu</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {opportunities.map(o => (
                <tr key={o.id} className="hover:bg-gray-50 align-top">
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-800">{o.title}</div>
                    {o.contact_name && <div className="text-xs text-gray-500">{o.contact_name}</div>}
                    {o.stage === 'lost' && o.lost_reason && <div className="text-xs text-red-500">Neden: {o.lost_reason}</div>}
                  </td>
                  <td className="px-4 py-3"><StageBadge stage={o.stage} /></td>
                  <td className="px-4 py-3 text-right text-sm font-medium">{formatMoney(o.value, o.currency)}</td>
                  <td className="px-4 py-3 text-right text-sm text-gray-600">%{o.probability}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(o.expected_close_date)}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex flex-wrap gap-1">
                      {o.quotes.map(q => (
                        <button key={q.id} onClick={() => navigate(`/quotes/${q.id}`)} className="font-mono text-xs text-blue-600 hover:underline" title={q.status}>{q.quote_number}</button>
                      ))}
                    </div>
                    {OPEN_STAGES.includes(o.stage) && (
                      <button onClick={() => navigate(`/quotes/new?customer_id=${customerId}&opportunity_id=${o.id}`)} className="text-xs text-green-600 hover:text-green-800 font-medium">+ Teklif oluştur</button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{o.owner_name || '-'}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setActivityModal({ defaults: { type: 'task', opportunity_id: o.id } })} className="text-gray-600 hover:text-gray-800 text-sm mr-3">Görev</button>
                    <button onClick={() => setOppModal(o)} className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-3">Düzenle</button>
                    <button onClick={() => deleteOpportunity(o)} className="text-red-500 hover:text-red-700 font-medium text-sm">Sil</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {opportunities.length === 0 && <div className="text-center py-12 text-gray-400">Henüz fırsat yok</div>}
        </div>
      )}

      {tab === 'quotes' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Teklif No</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Proje</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Fırsat</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Tarih</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Durum</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">USD</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">TRY</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {quotes.map(q => (
                <tr key={q.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/quotes/${q.id}`)}>
                  <td className="px-4 py-3 font-mono text-sm font-medium text-blue-600">{q.quote_number}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{q.project_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{oppTitle(q.opportunity_id) || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(q.quote_date)}</td>
                  <td className="px-4 py-3"><QuoteStatusBadge status={q.status} /></td>
                  <td className="px-4 py-3 text-right text-sm text-blue-700">{q.subtotal_usd > 0 ? `$${q.subtotal_usd.toFixed(2)}` : '—'}</td>
                  <td className="px-4 py-3 text-right text-sm">{q.subtotal_try > 0 ? `₺${q.subtotal_try.toFixed(2)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {quotes.length === 0 && <div className="text-center py-12 text-gray-400">Bu müşteri için teklif yok</div>}
        </div>
      )}

      {tab === 'contacts' && (
        <div>
          <div className="flex justify-end mb-3">
            <button onClick={() => setContactForm({ ...emptyContact })} className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium">+ Yeni Kişi</button>
          </div>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Ad Soyad</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Ünvan</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Telefon</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">E-posta</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Not</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {contacts.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">
                      {c.name} {c.is_primary && <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Birincil</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.title || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.phone ? <a href={`tel:${c.phone}`} className="hover:text-blue-600">{c.phone}</a> : '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{c.email ? <a href={`mailto:${c.email}`} className="hover:text-blue-600">{c.email}</a> : '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{c.notes || '-'}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => setContactForm({ ...c })} className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-3">Düzenle</button>
                      <button onClick={() => deleteContact(c)} className="text-red-500 hover:text-red-700 font-medium text-sm">Sil</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {contacts.length === 0 && <div className="text-center py-12 text-gray-400">Henüz kişi eklenmemiş</div>}
          </div>
        </div>
      )}

      {contactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-4">{contactForm.id ? 'Kişiyi Düzenle' : 'Yeni Kişi'}</h3>
            <form onSubmit={saveContact}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad *</label>
                  <input required value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ünvan</label>
                  <input value={contactForm.title || ''} onChange={e => setContactForm({ ...contactForm, title: e.target.value })} className="w-full border rounded px-3 py-2" placeholder="ör. Satın Alma Müdürü" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                  <input value={contactForm.phone || ''} onChange={e => setContactForm({ ...contactForm, phone: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                  <input type="email" value={contactForm.email || ''} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Not</label>
                  <textarea value={contactForm.notes || ''} onChange={e => setContactForm({ ...contactForm, notes: e.target.value })} className="w-full border rounded px-3 py-2" rows={2} />
                </div>
                <label className="col-span-2 flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!contactForm.is_primary} onChange={e => setContactForm({ ...contactForm, is_primary: e.target.checked })} />
                  Birincil kişi
                </label>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 font-medium">Kaydet</button>
                <button type="button" onClick={() => setContactForm(null)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 font-medium">İptal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {oppModal && (
        <OpportunityModal
          opportunity={oppModal === 'new' ? null : oppModal}
          customerId={customerId}
          onClose={() => setOppModal(null)}
          onSaved={() => { setOppModal(null); fetchAll(); }}
        />
      )}

      {activityModal && (
        <ActivityModal
          activity={activityModal.activity}
          defaults={activityModal.defaults}
          customerId={customerId}
          onClose={() => setActivityModal(null)}
          onSaved={() => { setActivityModal(null); fetchAll(); }}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, alert }) {
  return (
    <div className={`rounded-lg shadow p-4 ${alert ? 'bg-red-50 border border-red-200' : 'bg-white'}`}>
      <p className={`text-sm ${alert ? 'text-red-600' : 'text-gray-500'}`}>{label}</p>
      <p className={`text-2xl font-bold ${alert ? 'text-red-700' : 'text-gray-800'}`}>{value}</p>
      {sub && <p className={`text-xs mt-1 truncate ${alert ? 'text-red-600' : 'text-gray-500'}`}>{sub}</p>}
    </div>
  );
}

function ActivityRow({ a, onToggle, onEdit, onDelete, navigate }) {
  const t = activityTypeInfo(a.type);
  const isSystem = a.type === 'system';
  const isTask = a.type === 'task';
  return (
    <div className={`px-4 py-3 flex gap-3 ${isSystem ? 'bg-gray-50' : ''}`}>
      <div className="text-lg leading-6">{t.icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {isTask && <input type="checkbox" checked={a.is_done} onChange={() => onToggle(a)} />}
          <span className={`text-sm ${isSystem ? 'text-gray-500' : 'font-medium text-gray-800'} ${isTask && a.is_done ? 'line-through text-gray-400' : ''}`}>{a.subject}</span>
          {isTask && a.due_date && (
            <span className={`text-xs px-2 py-0.5 rounded ${isOverdue(a) ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>{formatDate(a.due_date)}</span>
          )}
        </div>
        {a.description && <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{a.description}</div>}
        <div className="text-xs text-gray-400 mt-1 flex flex-wrap gap-x-3">
          <span>{formatDateTime(a.created_at)}</span>
          {a.created_by_name && <span>{a.created_by_name}</span>}
          {a.assigned_to_name && isTask && <span>→ {a.assigned_to_name}</span>}
          {a.contact_name && <span>👤 {a.contact_name}</span>}
          {a.opportunity_title && <span>💼 {a.opportunity_title}</span>}
          {a.quote_number && <button onClick={() => navigate(`/quotes/${a.quote_id}`)} className="text-blue-500 hover:underline">📄 {a.quote_number}</button>}
        </div>
      </div>
      {!isSystem && (
        <div className="flex gap-2 text-xs whitespace-nowrap">
          <button onClick={onEdit} className="text-blue-600 hover:text-blue-800">Düzenle</button>
          <button onClick={() => onDelete(a)} className="text-red-500 hover:text-red-700">Sil</button>
        </div>
      )}
    </div>
  );
}
